import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  XMarkIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PlusIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { auth } from '../../../firebase';
import { useDriver } from '../../../contexts/DriverContext';
import InvoiceDataModal from './InvoiceDataModal';

const TopUpModal = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { driverAccount, hasInvoiceData, refreshDriverAccount } = useDriver();
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  // Steps: 1=Amount, 2=Payment Method, 3=Confirmation, 4=Success, 5=Error
  const [step, setStep] = useState(1);
  const [amount, setAmount] = useState('');
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [calculatedTotal, setCalculatedTotal] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [saveNewCard, setSaveNewCard] = useState(true);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const predefinedAmounts = [50, 100, 200, 500];

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [isOpen]);

  // Check invoice data and URL parameters when modal opens
  useEffect(() => {
    if (isOpen) {
      // Check if invoice data is missing
      if (!hasInvoiceData) {
        setShowInvoiceModal(true);
        return;
      }

      // Check URL parameters for payment status after redirect (SUCCESS/CANCEL handling)
      const params = new URLSearchParams(window.location.search);
      const status = params.get('topup_status');

      if (status === 'success') {
        setStep(4); // Success step
        setSuccessMessage('Contul tău a fost alimentat cu succes!');
        refreshDriverAccount();
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (status === 'cancel') {
        setStep(5); // Error step
        setError('Plata a fost anulată. Te rugăm să încerci din nou.');
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, [isOpen, hasInvoiceData, refreshDriverAccount]);

  // Fetch payment methods when modal opens
  useEffect(() => {
    if (isOpen && step === 2) {
      fetchPaymentMethods();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, step]);

  // Fetch saved payment methods from GET /driver/payment/methods
  const fetchPaymentMethods = async () => {
    setLoadingPaymentMethods(true);
    try {
      const idToken = await auth.currentUser.getIdToken(true);
      const response = await fetch('https://api.servicegest.ro/driver/payment/methods', {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });
      const data = await response.json();
      console.log('Payment Methods Response:', data); // Debug log
      setPaymentMethods(data || []);

      // Auto-select first payment method if available, otherwise leave as null (new card)
      if (data && data.length > 0) {
        const firstMethodId = data[0].id;
        console.log('Auto-selecting first payment method:', firstMethodId, 'Type:', typeof firstMethodId);
        setSelectedPaymentMethodId(firstMethodId);
        console.log('After setting, selectedPaymentMethodId should be:', firstMethodId);
      } else {
        console.log('No payment methods found, defaulting to new card');
        setSelectedPaymentMethodId(null);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      setPaymentMethods([]);
      setSelectedPaymentMethodId(null);
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  // Calculate total with fees using GET /driver/payment/top-up/calculate
  const calculateTotal = async (amountValue) => {
    if (!amountValue || parseFloat(amountValue) < 5) {
      setCalculatedTotal(null);
      return;
    }

    setCalculating(true);
    try {
      const idToken = await auth.currentUser.getIdToken(true);
      const response = await fetch(
        `https://api.servicegest.ro/driver/payment/top-up/calculate?amount=${amountValue}`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      const data = await response.json();
      setCalculatedTotal(data);
    } catch (error) {
      console.error('Error calculating total:', error);
      setCalculatedTotal(null);
    } finally {
      setCalculating(false);
    }
  };

  const handleAmountSelect = (value) => {
    setSelectedAmount(value);
    setAmount(value.toString());
    calculateTotal(value);
  };

  const handleCustomAmount = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d+\.?\d{0,2}$/.test(value)) {
      setAmount(value);
      setSelectedAmount(null);
      if (value && parseFloat(value) >= 5) {
        calculateTotal(value);
      } else {
        setCalculatedTotal(null);
      }
    }
  };

  const handleNextStep = async () => {
    if (!amount || parseFloat(amount) < 5) {
      setError(t('topUpModal.amountError'));
      return;
    }

    setError('');

    // Calculate total if not already calculated
    if (!calculatedTotal) {
      await calculateTotal(amount);
    }

    setStep(2); // Go to payment method selection (useEffect will fetch payment methods)
  };

  const handlePaymentMethodNext = () => {
    setError('');
    setStep(3); // Go to confirmation
  };

  // POST /driver/payment/top-up - Handles 200 OK (redirect) and 202 Accepted (success)
  const processTopUp = async () => {
    setLoading(true);
    setError('');

    try {
      const idToken = await auth.currentUser.getIdToken(true);
      // Build current URL for success/cancel redirects
      //   const baseUrl = window.location.origin + window.location.pathname;
      const baseUrl = "https://app.servicegest.ro/driver";
      const successUrl = `${baseUrl}?topup_status=success`;
      const cancelUrl = `${baseUrl}?topup_status=cancel`;
      console.log('ID Token:', idToken); // Debug log
      const payload = {
        amount: parseFloat(amount),
        paymentMethodId: selectedPaymentMethodId,
        successUrl,
        cancelUrl,
        savePaymentMethod: selectedPaymentMethodId ? false : saveNewCard,
      };

      console.log('Payload:', payload); // Debug log

      const response = await fetch('https://api.servicegest.ro/driver/payment/top-up', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      console.log('Response Status:', response.status); // Debug log

      // Handle 202 Accepted - Off-Session Payment completed successfully (no JSON body)
      if (response.status === 202) {
        console.log('202 Accepted - Payment processed successfully with saved card');
        setLoading(false); // Stop loading immediately
        setStep(4);
        setSuccessMessage(t('topUpModal.accountTopUp'));
        refreshDriverAccount();
        // Don't call onSuccess() here - let user see the success message first
        return; // Exit early
      }
      // Handle 200 OK - Redirect to Stripe Checkout (On-Session Payment)
      else if (response.status === 200) {
        const data = await response.json();
        console.log('Top-Up Response Data:', data); // Debug log

        if (data.url) {
          console.log('Redirecting to Stripe Checkout:', data.url);
          window.location.href = data.url;
          return; // Exit early
        } else {
          // 200 but no URL - treat as success
          console.log('200 OK but no URL - treating as success');
          setLoading(false); // Stop loading immediately
          setStep(4);
          setSuccessMessage(t('topUpModal.accountTopUp'));
          refreshDriverAccount();
          // Don't call onSuccess() here - let user see the success message first
          return; // Exit early
        }
      } else {
        // Unexpected status code
        console.error('Unexpected response status:', response.status);
        throw new Error(`Unexpected status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error processing top-up:', error);
      setStep(5);
      setError(
        error.message ||
        t('topUpModal.paymentError')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setAmount('');
    setSelectedAmount(null);
    setCalculatedTotal(null);
    // Don't reset selectedPaymentMethodId - let it remember the last selection
    // setSelectedPaymentMethodId(null);
    setSaveNewCard(true);
    setError('');
    setSuccessMessage('');
    onClose();
  };

  const handleSuccessClose = () => {
    handleClose();
    if (onSuccess) onSuccess();
  };

  const handleInvoiceDataSuccess = async () => {
    // Refresh driver account to get updated invoice data
    await refreshDriverAccount();
    setShowInvoiceModal(false);
    // Continue with the top-up flow
  };

  const handleInvoiceDataClose = () => {
    setShowInvoiceModal(false);
    // Close the top-up modal as well since invoice data is required
    handleClose();
  };

  if (!isOpen) return null;

  // If invoice modal is shown, don't render the main TopUpModal content
  if (showInvoiceModal) {
    return (
      <InvoiceDataModal
        isOpen={showInvoiceModal}
        onClose={handleInvoiceDataClose}
        onSuccess={handleInvoiceDataSuccess}
        driverAccount={driverAccount}
      />
    );
  }

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-20 bg-slate-100/10 dark:bg-slate-900/10 backdrop-blur-sm flex justify-center items-center p-2"
        onClick={step !== 4 && step !== 5 ? handleClose : undefined}
        style={{ overscrollBehavior: 'none' }}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-2 touch-none" style={{ overscrollBehavior: 'none' }}>
        <div
          className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col border dark:border-slate-700"
          onClick={(e) => e.stopPropagation()}
          style={{ overscrollBehavior: 'contain' }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 rounded-t-xl bg-purple-50 dark:bg-purple-900/20 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  {step === 4 ? (
                    <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                  ) : step === 5 ? (
                    <XCircleIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
                  ) : (
                    <CreditCardIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  )}
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-white">
                    {step === 1 && t('topUpModal.title')}
                    {step === 2 && t('topUpModal.paymentMethod')}
                    {step === 3 && t('topUpModal.confirmation')}
                    {step === 4 && t('topUpModal.success')}
                    {step === 5 && t('topUpModal.error')}
                  </h2>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4 overflow-y-auto flex-1">
            {/* Current Balance - Show in steps 1-3 */}
            {step <= 3 && (
              <div className="mb-4 px-4 py-2 bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 rounded-xl border border-purple-200 dark:border-purple-700">
                <p className="text-sm text-purple-600 dark:text-purple-400">{t('topUpModal.currentBalance')}</p>
                <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                  {(driverAccount?.balance || 0).toFixed(2)} RON
                </p>
              </div>
            )}

            {/* Step 1: Amount Selection */}
            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                    {t('topUpModal.selectAmount')}
                  </label>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-3 flex items-center gap-1">
                    {t('topUpModal.stripeNotice')}
                  </p>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {predefinedAmounts.map((value) => (
                      <button
                        key={value}
                        onClick={() => handleAmountSelect(value)}
                        className={`p-4 rounded-xl border-2 transition-all ${selectedAmount === value
                          ? 'border-purple-600 dark:border-purple-400 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                          : 'border-slate-200 dark:border-slate-700 hover:border-purple-300 dark:hover:border-purple-600 text-slate-700 dark:text-slate-300'
                          }`}
                      >
                        <span className="text-xl font-bold">{value} RON</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {t('topUpModal.customAmount')}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      inputMode="numeric"
                      value={amount}
                      onChange={handleCustomAmount}
                      placeholder="0.00"
                      className="w-full px-4 py-3 pr-16 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:border-purple-600 dark:focus:border-purple-400 focus:outline-none transition-colors text-lg font-semibold"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 font-medium">
                      RON
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    {t('topUpModal.minimumAmount')}
                  </p>
                </div>

                {/* Show calculated total with fees */}
                {/* {calculatedTotal && (
                  <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600 dark:text-slate-400">Sumă adăugată:</span>
                        <span className="font-medium text-slate-900 dark:text-white">
                          {parseFloat(amount).toFixed(2)} RON
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600 dark:text-slate-400">Taxe procesare:</span>
                        <span className="font-medium text-slate-900 dark:text-white">
                          {(calculatedTotal - parseFloat(amount)).toFixed(2)} RON
                        </span>
                      </div>
                      <div className="pt-2 border-t border-slate-200 dark:border-slate-700 flex justify-between">
                        <span className="font-semibold text-slate-900 dark:text-white">Total de plată:</span>
                        <span className="font-bold text-purple-600 dark:text-purple-400">
                          {parseFloat(calculatedTotal).toFixed(2)} RON
                        </span>
                      </div>
                    </div>
                  </div>
                )} */}

                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                    <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                  </div>
                )}

                <button
                  onClick={handleNextStep}
                  disabled={!amount || parseFloat(amount) < 5 || calculating}
                  className={`w-full py-3 rounded-lg font-semibold text-white ${!amount || parseFloat(amount) < 5 || calculating
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-700 shadow-lg'
                    }`}
                >
                  {calculating ? t('topUpModal.calculating') : t('topUpModal.continue')}
                </button>
              </div>
            )}

            {/* Step 2: Payment Method Selection */}
            {step === 2 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {t('topUpModal.choosePaymentMethod')}
                  </label>

                  {loadingPaymentMethods ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{t('topUpModal.loadingMethods')}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {paymentMethods.map((method) => (
                        <button
                          key={method.id}
                          onClick={() => {
                            console.log('Clicked on payment method:', method.id);
                            setSelectedPaymentMethodId(method.id);
                          }}
                          className={`w-full px-4 py-2 rounded-xl border-2 transition-all text-left flex items-center justify-between ${selectedPaymentMethodId === method.id
                            ? 'border-purple-600 dark:border-purple-400 bg-purple-50 dark:bg-purple-900/30'
                            : 'border-slate-200 dark:border-slate-700 hover:border-purple-300 dark:hover:border-purple-600'
                            }`}
                        >
                          <div className="flex items-center gap-3">
                            <CreditCardIcon className="h-6 w-6 text-slate-600 dark:text-slate-400" />
                            <div>
                              <p className="font-medium text-slate-900 dark:text-white">
                                {method.brand.toUpperCase()} •••• {method.last4}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                Expires {method.expMonth}/{method.expYear}
                              </p>
                            </div>
                          </div>
                          {selectedPaymentMethodId === method.id && (
                            <CheckCircleIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                          )}
                        </button>
                      ))}

                      {/* Add new card option */}
                      <button
                        onClick={() => {
                          console.log('Clicked on Add new card');
                          setSelectedPaymentMethodId(null);
                        }}
                        className={`w-full px-4 py-2 rounded-xl border-2 transition-all text-left flex items-center justify-between ${selectedPaymentMethodId === null
                          ? 'border-purple-600 dark:border-purple-400 bg-purple-50 dark:bg-purple-900/30'
                          : 'border-slate-200 dark:border-slate-700 hover:border-purple-300 dark:hover:border-purple-600'
                          }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                            <PlusIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">
                              {t('topUpModal.addNewCard')}
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              {t('topUpModal.redirectNotice')}
                            </p>
                          </div>
                        </div>
                        {selectedPaymentMethodId === null && (
                          <CheckCircleIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                        )}
                      </button>

                      {/* Save card checkbox - only show when adding new card */}
                      {selectedPaymentMethodId === null && (
                        <label className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg cursor-pointer">
                          <input
                            type="checkbox"
                            checked={saveNewCard}
                            onChange={(e) => setSaveNewCard(e.target.checked)}
                            className="mt-0.5 h-4 w-4 text-purple-600 rounded focus:ring-purple-500"
                          />
                          <span className="text-sm text-blue-900 dark:text-blue-100">
                            {t('topUpModal.saveCard')}
                          </span>
                        </label>
                      )}

                      {/* Stripe Security Notice */}
                      <div className="flex items-start gap-2 p-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-lg">
                        <svg className="w-5 h-5 text-slate-500 dark:text-slate-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        <div className="text-xs text-slate-600 dark:text-slate-400">
                          <p className="font-medium mb-1">{t('topUpModal.securePayment')}</p>
                          <p>{t('topUpModal.securityNotice')}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                    <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => setStep(1)}
                    className="flex-1 py-3 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 border border-slate-300 dark:border-slate-600 font-medium rounded-lg transition-colors"
                  >
                    {t('topUpModal.back')}
                  </button>
                  <button
                    onClick={handlePaymentMethodNext}
                    className="flex-1 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg shadow-lg transition-colors"
                  >
                    {t('topUpModal.continue')}
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Confirmation */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="px-4 py-2 bg-slate-50 dark:bg-slate-900 rounded-xl">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600 dark:text-slate-400">{t('topUpModal.amountToAdd')}:</span>
                    <span className="text-lg font-bold text-slate-900 dark:text-white">
                      {parseFloat(amount).toFixed(2)} RON
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600 dark:text-slate-400 text-sm">{t('topUpModal.processingFees')}:</span>
                    <span className="text-slate-900 dark:text-white text-sm">
                      {calculatedTotal ? (calculatedTotal - parseFloat(amount)).toFixed(2) : '0.00'} RON
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-3 border-t border-slate-200 dark:border-slate-700">
                    <span className="font-semibold text-slate-900 dark:text-white">{t('topUpModal.totalToPay')}:</span>
                    <span className="text-xl font-bold text-purple-600 dark:text-purple-400">
                      {calculatedTotal ? parseFloat(calculatedTotal).toFixed(2) : '0.00'} RON
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-3 border-t border-slate-200 dark:border-slate-700">
                    <span className="text-slate-600 dark:text-slate-400">{t('topUpModal.newBalance')}:</span>
                    <span className="text-lg font-bold text-green-600 dark:text-green-400">
                      {((driverAccount?.balance || 0) + parseFloat(amount)).toFixed(2)} RON
                    </span>
                  </div>
                </div>

                {/* Payment method info */}
                <div className="px-4 py-2 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">
                    {t('topUpModal.paymentMethodInfo')}
                  </h3>
                  {selectedPaymentMethodId ? (
                    (() => {
                      const method = paymentMethods.find(m => m.id === selectedPaymentMethodId);
                      return method ? (
                        <div className="flex items-center gap-2">
                          <CreditCardIcon className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                          <span className="text-slate-900 dark:text-white">
                            {method.brand.toUpperCase()} •••• {method.last4}
                          </span>
                        </div>
                      ) : null;
                    })()
                  ) : (
                    <div className="flex items-center gap-2">
                      <PlusIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      <span className="text-slate-900 dark:text-white">{t('topUpModal.newCard')}</span>
                    </div>
                  )}
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                    <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                  </div>
                )}

                {/* Security Notice */}
                <div className="flex items-start gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-green-800 dark:text-green-200">
                    <p className="font-medium">Plată securizată</p>
                    <p>Toate datele tale bancare sunt protejate prin Stripe, conform standardelor PCI DSS.</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setStep(2)}
                    disabled={loading}
                    className="flex-1 py-3 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 border border-slate-300 dark:border-slate-600 font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    Înapoi
                  </button>
                  <button
                    onClick={processTopUp}
                    disabled={loading}
                    className={`flex-1 py-3 rounded-lg font-semibold text-white ${loading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-purple-600 hover:bg-purple-700 shadow-lg'
                      }`}
                  >
                    {loading ? t('topUpModal.processing') : t('topUpModal.payNow')}
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Success */}
            {step === 4 && (
              <div className="space-y-4 text-center py-6">
                <div className="flex justify-center">
                  <div className="p-4 bg-green-100 dark:bg-green-900/30 rounded-full">
                    <CheckCircleIcon className="h-16 w-16 text-green-600 dark:text-green-400" />
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                    {t('topUpModal.success')}!
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400">
                    {successMessage}
                  </p>
                </div>

                <div className="p-4 bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 rounded-xl border border-green-200 dark:border-green-700">
                  <p className="text-sm text-green-600 dark:text-green-400 mb-1">{t('topUpModal.newBalance')}</p>
                  <p className="text-3xl font-bold text-green-700 dark:text-green-300">
                    {(driverAccount?.balance || 0).toFixed(2)} RON
                  </p>
                </div>

                <button
                  onClick={handleSuccessClose}
                  className="w-full py-3 rounded-lg font-semibold text-white bg-green-600 hover:bg-green-700 shadow-lg transition-colors"
                >
                  {t('common.close')}
                </button>
              </div>
            )}

            {/* Step 5: Error */}
            {step === 5 && (
              <div className="space-y-4 text-center py-6">
                <div className="flex justify-center">
                  <div className="p-4 bg-red-100 dark:bg-red-900/30 rounded-full">
                    <XCircleIcon className="h-16 w-16 text-red-600 dark:text-red-400" />
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                    {t('topUpModal.error')}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400">
                    {error}
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleClose}
                    className="flex-1 py-3 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 border border-slate-300 dark:border-slate-600 font-medium rounded-lg transition-colors"
                  >
                    {t('common.close')}
                  </button>
                  <button
                    onClick={() => {
                      setStep(1);
                      setError('');
                    }}
                    className="flex-1 py-3 rounded-lg font-semibold text-white bg-purple-600 hover:bg-purple-700 shadow-lg transition-colors"
                  >
                    {t('common.retry')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TopUpModal;

